import { useState, useEffect } from 'react';
import { Check, Crown, Users, Building, Sparkles } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  DialogScrollArea,
} from './ui/dialog';
import { useTranslation } from 'react-i18next';
import { useBusinessAnalytics } from '../utils/analytics';
import { couponService } from '../services/coupon';
import { dctw26BlocksApplied, isDctw26Code } from '../lib/dctw26Coupon';

interface PricingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPlan: (planId: string, billingCycle?: 'monthly' | 'annual', couponCode?: string) => void | Promise<void>;
  currentTier?: string;
}

// Updated PricingTier interface for request focus
interface PricingTier {
  id: string;
  name: string;
  price: number | 'Custom';
  features: string[];
  buttonText: string;
  buttonVariant: 'primary' | 'secondary' | 'enterprise';
}

export default function PricingModal({ isOpen, onClose, onSelectPlan, currentTier = 'free' }: PricingModalProps) {
  const { t } = useTranslation();
  const { trackUpgradeModalView, trackPlanSelection, trackFeatureUsed } = useBusinessAnalytics();

  // Normalize currentTier to lowercase for comparison (avoid mutating prop)
  const normalizedTier = currentTier.toLowerCase();
  const [isAnnual, setIsAnnual] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [couponDiscount, setCouponDiscount] = useState<number | null>(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [dctwRestriction, setDctwRestriction] = useState(false);

  // Track modal view when opened (defensive: don't block render if analytics throws)
  useEffect(() => {
    if (isOpen) {
      try {
        trackUpgradeModalView({
          source: 'pricing_modal',
          current_tier: normalizedTier,
          upgrade_context: 'general',
          trigger_reason: 'pricing_modal_opened'
        });
      } catch (_e) {
        // Analytics failure must not prevent modal from showing
      }
    }
  }, [isOpen, normalizedTier, trackUpgradeModalView]);

  const getButtonStyles = (variant: string, tierIsCurrent: boolean) => {
    if (tierIsCurrent) {
      return 'bg-gray-100 text-gray-500 cursor-not-allowed';
    }

    switch (variant) {
      case 'primary':
        return 'bg-indigo-600 hover:bg-indigo-700 text-white';
      case 'secondary':
        return 'bg-white border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50';
      case 'enterprise':
        return 'bg-slate-600 hover:bg-slate-700 text-white';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getTierDescription = (tierId: string) => {
    switch (tierId) {
      case 'free':
        return t('tiers.free.description', { ns: 'pricing' }) as string;
      case 'starter':
        return t('tiers.starter.description', { ns: 'pricing' }) as string;
      case 'pro':
        return t('tiers.pro.description', { ns: 'pricing' }) as string;
      case 'team':
        return t('tiers.pro.description', { ns: 'pricing' }) as string;
      case 'enterprise':
        return t('tiers.enterprise.description', { ns: 'pricing' }) as string;
      default:
        return '';
    }
  };

  const getTierLimitRequests = (tierId: string) => {
    switch (tierId) {
      case 'free':
        return '';
      case 'starter':
        return t('tiers.starter.limits.requests', { ns: 'pricing' }) as string;
      case 'pro':
        return t('tiers.pro.limits.requests', { ns: 'pricing' }) as string;
      case 'team':
        return t('tiers.pro.limits.requests', { ns: 'pricing' }) as string;
      case 'enterprise':
        return t('tiers.enterprise.limits.requests', { ns: 'pricing' }) as string;
      default:
        return '';
    }
  };

  const getTierLimitSpaces = (tierId: string) => {
    switch (tierId) {
      case 'free':
        return t('tiers.free.limits.spaces', { ns: 'pricing' }) as string;
      case 'starter':
        return t('tiers.starter.limits.spaces', { ns: 'pricing' }) as string;
      case 'pro':
        return t('tiers.pro.limits.spaces', { ns: 'pricing' }) as string;
      case 'team':
        return t('tiers.pro.limits.spaces', { ns: 'pricing' }) as string;
      case 'enterprise':
        return t('tiers.enterprise.limits.spaces', { ns: 'pricing' }) as string;
      default:
        return '';
    }
  };

  const getTierLimitSupport = (tierId: string) => {
    switch (tierId) {
      case 'free':
        return t('tiers.free.limits.support', { ns: 'pricing' }) as string;
      case 'starter':
        return t('tiers.starter.limits.support', { ns: 'pricing' }) as string;
      case 'pro':
        return t('tiers.pro.limits.support', { ns: 'pricing' }) as string;
      case 'team':
        return t('tiers.pro.limits.support', { ns: 'pricing' }) as string;
      case 'enterprise':
        return t('tiers.enterprise.limits.support', { ns: 'pricing' }) as string;
      default:
        return '';
    }
  };

  // Calculate discounted price
  const getDiscountedPrice = (originalPrice: number | 'Custom'): { original: number | 'Custom', discounted: number | 'Custom', savings: number } => {
    if (originalPrice === 'Custom' || !couponDiscount) {
      return { original: originalPrice, discounted: originalPrice, savings: 0 };
    }

    const original = originalPrice as number;
    const discounted = Math.round(original * (1 - couponDiscount / 100));
    const savings = original - discounted;

    return { original, discounted, savings };
  };

  // Validate and apply coupon code
  const validateAndApplyCoupon = async (code: string) => {
    if (!code.trim()) {
      setCouponError('Please enter a coupon code');
      return;
    }

    if (dctw26BlocksApplied(code, isAnnual ? 'annual' : 'monthly')) {
      setCouponApplied(false);
      setCouponDiscount(null);
      setCouponError('');
      setDctwRestriction(true);
      return;
    }

    setDctwRestriction(false);
    setCouponLoading(true);
    setCouponError('');

    try {
      const response = await couponService.validateCoupon({
        coupon_code: code,
        tier: 'pro',
        billing_cycle: isAnnual ? 'annual' : 'monthly'
      });

      if (response.valid && response.discount_percent) {
        setCouponApplied(true);
        setCouponDiscount(response.discount_percent);
        setCouponError('');
        try {
          trackFeatureUsed('coupon_code_applied', {
            coupon_code: code,
            current_tier: normalizedTier,
            source: 'pricing_modal',
            billing_cycle: isAnnual ? 'annual' : 'monthly',
            discount_percentage: response.discount_percent
          });
        } catch (_e) {
          // Analytics failure must not affect coupon application
        }
      } else {
        setCouponError(response.error_message || 'Invalid coupon code');
        setCouponApplied(false);
        setCouponDiscount(null);
      }
    } catch (error) {
      console.error('Error validating coupon:', error);
      setCouponError('Error validating coupon code');
      setCouponApplied(false);
      setCouponDiscount(null);
    } finally {
      setCouponLoading(false);
    }
  };

  // New tiers data with i18n keys
  const tiers: PricingTier[] = [
    {
      id: 'free',
      name: t('tiers.free.name', { ns: 'pricing' }) as string,
      price: 0,
      features: [
        t('tiers.free.features.memories', { ns: 'pricing' }) as string,
        t('tiers.free.features.spaces', { ns: 'pricing' }) as string,
        t('tiers.free.features.integrations', { ns: 'pricing' }) as string,
        t('tiers.free.features.support', { ns: 'pricing' }) as string
      ],
      buttonText: t('tiers.free.buttonText', { ns: 'pricing' }) as string,
      buttonVariant: 'secondary'
    },
    {
      id: 'starter',
      name: t('tiers.starter.name', { ns: 'pricing' }) as string,
      price: isAnnual ? 90 : 9,
      features: [
        t('tiers.starter.features.memories', { ns: 'pricing' }) as string,
        t('tiers.starter.features.requests', { ns: 'pricing' }) as string,
        t('tiers.starter.features.spaces', { ns: 'pricing' }) as string,
        t('tiers.starter.features.integrations', { ns: 'pricing' }) as string,
        t('tiers.starter.features.support', { ns: 'pricing' }) as string
      ],
      buttonText: t('tiers.starter.buttonText', { ns: 'pricing' }) as string,
      buttonVariant: 'primary'
    },
    {
      id: 'pro',
      name: t('tiers.pro.name', { ns: 'pricing' }) as string,
      price: isAnnual ? 290 : 29,
      features: [
        t('tiers.pro.features.memories', { ns: 'pricing' }) as string,
        t('tiers.pro.features.requests', { ns: 'pricing' }) as string,
        t('tiers.pro.features.spaces', { ns: 'pricing' }) as string,
        t('tiers.pro.features.multimodal', { ns: 'pricing' }) as string,
        t('tiers.pro.features.integrations', { ns: 'pricing' }) as string,
        t('tiers.pro.features.support', { ns: 'pricing' }) as string
      ],
      buttonText: t('tiers.pro.buttonText', { ns: 'pricing' }) as string,
      buttonVariant: 'primary'
    },
    {
      id: 'enterprise',
      name: t('tiers.enterprise.name', { ns: 'pricing' }) as string,
      price: 'Custom',
      features: [
        t('tiers.enterprise.features.requests', { ns: 'pricing' }) as string,
        t('tiers.enterprise.features.video', { ns: 'pricing' }) as string,
        t('tiers.enterprise.features.compliance', { ns: 'pricing' }) as string,
        t('tiers.enterprise.features.support', { ns: 'pricing' }) as string
      ],
      buttonText: t('tiers.enterprise.buttonText', { ns: 'pricing' }) as string,
      buttonVariant: 'enterprise'
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent variant="scrollable" className="max-w-7xl">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 pr-12">
          <DialogTitle className="text-3xl font-bold text-gray-900">{t('modal.title', { ns: 'pricing' }) as string}</DialogTitle>
          <DialogDescription className="text-gray-600">
            {t('modal.description', { ns: 'pricing' }) as string}
          </DialogDescription>

          {/* Billing Toggle */}
          <div className="flex items-center justify-center mt-6">
            <span className={`text-sm ${!isAnnual ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {t('billing.monthly', { ns: 'pricing' }) as string}
            </span>
            <button
              onClick={() => {
                const newBillingCycle = !isAnnual;
                try {
                  trackFeatureUsed('billing_cycle_toggle', {
                    previous_cycle: isAnnual ? 'annual' : 'monthly',
                    new_cycle: newBillingCycle ? 'annual' : 'monthly',
                    current_tier: normalizedTier,
                    source: 'pricing_modal'
                  });
                } catch (_e) {
                  // Analytics failure must not affect toggle
                }
                setIsAnnual(newBillingCycle);
                if (newBillingCycle && isDctw26Code(couponCode)) {
                  setCouponApplied(false);
                  setCouponDiscount(null);
                  setCouponError('');
                  setDctwRestriction(true);
                } else if (!newBillingCycle) {
                  setDctwRestriction(false);
                }
              }}
              aria-label={`${t('billing.monthly', { ns: 'pricing' })} / ${t('billing.annual', { ns: 'pricing' })}`}
              aria-pressed={isAnnual}
              className={`mx-3 relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                isAnnual ? 'bg-indigo-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  isAnnual ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-sm ${isAnnual ? 'text-gray-900 font-medium' : 'text-gray-500'}`}>
              {t('billing.annual', { ns: 'pricing' }) as string}
              <span className="ml-1 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                {t('billing.saveDiscount', { ns: 'pricing' }) as string}
              </span>
            </span>
          </div>

          {/* Coupon Code Field */}
          <div className="flex items-center justify-center mt-4">
            <div className="flex items-center space-x-2">
              <input
                type="text"
                placeholder="Enter coupon code"
                value={couponCode}
                onChange={(e) => {
                  setCouponCode(e.target.value);
                  setCouponError('');
                  setCouponApplied(false);
                  setDctwRestriction(false);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                disabled={couponApplied}
              />
              <button
                onClick={() => validateAndApplyCoupon(couponCode)}
                disabled={couponApplied || !couponCode.trim() || couponLoading}
                className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {couponLoading ? 'Validating...' : couponApplied && !dctwRestriction ? 'Applied' : 'Apply'}
              </button>
              {couponApplied && (
                <button
                  onClick={() => {
                    setCouponApplied(false);
                    setCouponCode('');
                    setCouponError('');
                    setCouponDiscount(null);
                    setCouponLoading(false);
                    setDctwRestriction(false);
                  }}
                  className="px-2 py-2 text-gray-500 hover:text-gray-700 text-sm"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          {dctwRestriction && (
            <div className="text-center mt-2">
              <span className="text-amber-700 text-sm font-medium">
                {t('modal.dctwProMonthlyOnly', { ns: 'pricing' }) as string}
              </span>
            </div>
          )}
          {couponError && (
            <div className="text-center mt-2">
              <span className="text-red-600 text-sm">{couponError}</span>
            </div>
          )}
          {couponApplied && !dctwRestriction && (
            <div className="text-center mt-2">
              <span className="text-green-600 text-sm font-medium">
                ✓ {couponDiscount}% discount applied successfully!
              </span>
            </div>
          )}
        </DialogHeader>

        <DialogScrollArea className="px-6">
        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pb-6">
          {tiers.map((tier) => {
            const tierIsCurrent = normalizedTier === tier.id;
            const IconComponent = tier.id === 'free' ? Sparkles :
                                 tier.id === 'starter' ? Crown :
                                 tier.id === 'pro' ? Users : Building;

            return (
              <div
                key={tier.id}
                className={`relative rounded-xl border-2 p-6 ${
                  tier.id === 'pro' || tier.id === 'enterprise'
                    ? 'border-indigo-600 ring-2 ring-indigo-600 ring-opacity-20'
                    : 'border-gray-200'
                } ${tierIsCurrent ? 'bg-gray-50' : 'bg-white'}`}
              >
                {tier.id === 'starter' && !tierIsCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-indigo-600 text-white px-3 py-1 text-sm font-medium rounded-full">
                      {t('badges.mostPopular', { ns: 'pricing' }) as string}
                    </span>
                  </div>
                )}

                {tierIsCurrent && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <span className="bg-green-600 text-white px-3 py-1 text-sm font-medium rounded-full">
                      {t('badges.currentPlan', { ns: 'pricing' }) as string}
                    </span>
                  </div>
                )}

                <div className="text-center">
                  <IconComponent className={`w-8 h-8 mx-auto ${tier.id === 'pro' || tier.id === 'enterprise' ? 'text-indigo-600' : 'text-gray-600'}`} />
                  <h3 className="text-xl font-semibold text-gray-900 mt-2">{tier.name}</h3>
                  <p className="text-gray-600 text-sm mt-1">
                    {getTierDescription(tier.id)}
                  </p>

                  <div className="mt-4">
                    {(() => {
                      const { original, discounted, savings } = getDiscountedPrice(tier.price);

                      if (original === 'Custom') {
                        return (
                          <span className="text-4xl font-bold text-gray-900">
                            {t('custom', { ns: 'pricing' }) as string}
                          </span>
                        );
                      }

                      const showDiscount =
                        couponApplied &&
                        !dctwRestriction &&
                        savings > 0 &&
                        (!isDctw26Code(couponCode) || tier.id === 'pro');

                      if (showDiscount) {
                        return (
                          <div className="text-center">
                            <div className="flex items-center justify-center space-x-2">
                              <span className="text-2xl font-bold text-gray-400 line-through">
                                ${original}
                              </span>
                              <span className="text-4xl font-bold text-green-600">
                                ${discounted}
                              </span>
                            </div>
                            <div className="text-sm text-green-600 font-medium mt-1">
                              Save ${savings} ({couponDiscount}% off)
                            </div>
                            <span className="text-gray-600 text-sm">
                              {isAnnual ? (t('perYear', { ns: 'pricing' }) as string) : (t('perMonth', { ns: 'pricing' }) as string)}
                            </span>
                          </div>
                        );
                      }

                      return (
                        <div className="text-center">
                          <span className="text-4xl font-bold text-gray-900">
                            ${original}
                          </span>
                          <span className="text-gray-600 ml-1">
                            {isAnnual ? (t('perYear', { ns: 'pricing' }) as string) : (t('perMonth', { ns: 'pricing' }) as string)}
                          </span>
                        </div>
                      );
                    })()}
                  </div>
                </div>

                {/* Limits */}
                <div className="mt-6 space-y-2">
                  <div className="text-sm text-gray-600">
                    {getTierLimitRequests(tier.id) ? (
                      <div className="font-medium">
                        {getTierLimitRequests(tier.id)}
                      </div>
                    ) : null}
                    <div>
                      {getTierLimitSpaces(tier.id)}
                    </div>
                    <div>
                      {getTierLimitSupport(tier.id)}
                    </div>
                  </div>
                </div>

                {/* Features */}
                <div className="mt-6">
                  <ul className="space-y-2">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start">
                        <Check className="w-4 h-4 text-green-500 mt-0.5 mr-2 flex-shrink-0" />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Button */}
                <div className="mt-8">
                  <button
                    onClick={() => {
                      if (!tierIsCurrent) {
                        const billingCycle = isAnnual ? 'annual' : 'monthly';
                        const codeIsDctw = isDctw26Code(couponCode);
                        if (dctw26BlocksApplied(couponCode, billingCycle, tier.id)) {
                          setCouponApplied(false);
                          setCouponDiscount(null);
                          setCouponError('');
                          setDctwRestriction(true);
                          return;
                        }
                        const appliedCoupon =
                          couponApplied &&
                          !dctwRestriction &&
                          (!codeIsDctw || (tier.id === 'pro' && billingCycle === 'monthly'))
                            ? couponCode
                            : undefined;

                        try {
                          trackPlanSelection({
                            source: 'pricing_modal',
                            selected_plan: tier.id,
                            billing_cycle: billingCycle,
                            current_tier: normalizedTier,
                            coupon_code: appliedCoupon
                          });
                        } catch (_e) {
                          // Analytics failure must not prevent plan selection
                        }

                        onSelectPlan(tier.id, billingCycle, appliedCoupon);
                      }
                    }}
                    disabled={tierIsCurrent}
                    className={`w-full py-3 px-4 rounded-lg font-medium transition-colors ${getButtonStyles(
                      tier.buttonVariant,
                      tierIsCurrent
                    )}`}
                  >
                    {tierIsCurrent ? t('badges.currentPlan', { ns: 'pricing' }) as string : tier.buttonText}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        </DialogScrollArea>

        <DialogFooter variant="scrollable" className="px-6 pb-6">
          <div className="w-full text-center">
            <p className="text-sm text-gray-600">
              {t('modal.footer.description', { ns: 'pricing' }) as string}
              <span className="block mt-1">
                {t('modal.footer.questions', { ns: 'pricing' }) as string} <a href="/support" className="text-indigo-600 hover:text-indigo-500">{t('modal.footer.contactTeam', { ns: 'pricing' }) as string}</a>
              </span>
            </p>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
